/**
 * Cleans up the per-TDO structured data left behind when a file is permanently
 * deleted (VE-26730).
 *
 * A TDO owns side data in several registries and `deleteTDO` removes none of
 * it — the rows are keyed by tdoId rather than parented to the TDO, so the
 * platform has nothing to cascade along. Whatever is not deleted here stays in
 * the org's registry indefinitely, referenced by nothing.
 *
 * Shared because the two hard-delete paths had drifted apart: the trash sweeper
 * cleaned up the redact-state SDO inline, and the immediate-delete path
 * (`trashRetentionDays: 0`) cleaned up nothing at all. One saga called from both
 * is the only arrangement that keeps them honest.
 *
 * Deliberately best-effort, and silent: it runs after a delete the user has
 * already been told succeeded, so a failure here must not surface as a failed
 * deletion — the file IS gone.
 *
 * A failure is PERMANENT, though, not deferred. Nothing sweeps orphans — the
 * TDO is deleted, so no later pass will ever enumerate it again. That is why
 * each step isolates its own failures (per registry, per file) rather than
 * abandoning the batch: every row skipped here is stranded for good, and the
 * console line is the only record that it happened.
 *
 * Covers the redact-state SDO, the status-history rows, and both comment
 * registries (VE-28551). NOT the TDO lock SDO: it holds no case content and
 * expires on its own.
 */
import { call, put, putResolve } from 'typed-redux-saga/macro';

import { TDOId } from '@cbsa/state/modules/universal';
import { purgeStatusHistoryForTdos } from '@common-modules/statusHistory/services';
import { deleteSDOBulk } from '@redact-modules/mainPage/services';
import {
  fetchSDOsBulkByTDOIds,
  FetchSDOsBulkByTDOIdsResponse,
  purgeCommentsForTdos,
} from '../services';

/**
 * The redact-state SDO — one per TDO, under `tdoRedactFileStatusRegistryId`.
 *
 * Awaits both thunks with `putResolve` rather than dispatching an action and
 * parking on a global `take`. redux-saga's channel is multicast and both
 * watchers are `takeEvery`, so two purges in flight — which deleting a folder
 * with two requests in it produces — woke on each other's terminal action: one
 * request's SDOs deleted twice, the other's stranded silently, which is the
 * exact outcome this saga exists to prevent. Same reasoning, and the same
 * remedy, as `deleteFolderIdsPermanently` (trash/sagas) already applies.
 *
 * `silent` keeps the shared terminal actions out of it: DELETE_SDO_BULK_FAILURE
 * has a global watcher that raises an error snackbar, which after a delete the
 * user was already told succeeded is a report of a failure they cannot act on.
 */
export function* purgeRedactStateSdos(tdoIds: TDOId[]) {
  let payload: FetchSDOsBulkByTDOIdsResponse | undefined;
  try {
    payload = (yield* putResolve(
      fetchSDOsBulkByTDOIds({ tdoIds, silent: true }) as never
    )) as FetchSDOsBulkByTDOIdsResponse | undefined;
  } catch {
    // Fixed vocabulary: the failure action carries a server-authored message.
    console.error('trash: redact-state lookup failed; SDOs retained', {
      tdoCount: tdoIds.length,
    });
    return;
  }

  const sdoIds = Object.values(payload ?? {}).reduce<string[]>(
    (ids, result) => {
      for (const sdo of result?.records ?? []) {
        ids.push(sdo.id);
      }
      return ids;
    },
    []
  );

  if (sdoIds.length === 0) {
    return;
  }
  try {
    yield* putResolve(deleteSDOBulk({ sdoIds, silent: true }) as never);
  } catch {
    console.error('trash: redact-state delete failed; SDOs retained', {
      sdoCount: sdoIds.length,
    });
  }
}

export function* purgeTdoSideData(tdoIds: TDOId[]) {
  if (tdoIds.length === 0) {
    return;
  }
  const ids = tdoIds.map(String);
  yield* call(purgeRedactStateSdos, tdoIds);
  // VE-26730: history rows carry the acting users' emails and the free text of
  // their correction notes, so stranding them is a retention problem rather
  // than clutter.
  yield* put(purgeStatusHistoryForTdos(ids) as never);
  // VE-28551: the same is true of comments, in both registries — the text and
  // its author, about a file that no longer exists.
  yield* put(purgeCommentsForTdos(ids) as never);
}
